import { Box, Stack } from '@mui/material'
import {
	cloneElement,
	ReactElement,
	ReactNode,
	useCallback,
	useState,
} from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { COLORS } from 'styles/colors'
import { TypographyPrimary16B } from 'ui/typography/TypographyPrimary16B'
import { TypographyPrimary500 } from 'ui/typography/TypographyPrimary500'

interface IChildrenSidebarLink {
	key: string
	value: string
}

interface IMainSidebarLink {
	icon: ReactElement
	textMenu: string | ReactNode
	subMenuItems?: IChildrenSidebarLink[]
	to?: string
	openNotifications?: boolean
}

export const MainSidebarLink = ({
	to,
	icon,
	textMenu,
	subMenuItems,
	openNotifications = false,
}: IMainSidebarLink) => {
	const [hoveredOnMainItems, setHoveredOnMainItems] = useState<boolean>(false)
	// const [isOpenMenuItem, setIsOpenMenuItem] = useState<boolean>(true)
	const location = useLocation()
	const navigate = useNavigate()
	const [hoveredOnSecondaryItems, setHoveredOnSecondaryItems] = useState<
		Record<number, boolean>
	>({})

	const normalizePath = (p?: string) => {
		if (!p) return ''
		let s = p.trim()
		if (!s.startsWith('/')) s = '/' + s
		s = s !== '/' ? s.replace(/\/+$/g, '') : s
		return s
	}
	const getSegmentCount = (p?: string) =>
		normalizePath(p).split('/').filter(Boolean).length

	// Односегментные корневые пути, которые должны подсвечиваться и на вложенных
	const DEEP_FOR_ONE_SEGMENT = new Set<string>(['/settings'])

	const isActiveDeep = (base?: string) => {
		if (!base) return false
		const norm = normalizePath(base)
		if (norm === '/') return location.pathname === '/'
		// Быстрый путь: строгое равенство или префикс с граничным слэшем
		if (
			location.pathname === norm ||
			location.pathname.startsWith(norm + '/')
		) {
			return true
		}
		// 1) вложенные пути через matchPath с wildcard
		if (matchPath({ path: `${norm}/*`, end: false }, location.pathname)) {
			return true
		}
		// 2) фоллбэк по последнему сегменту (если в конфиге укороченный путь)
		const lastSeg = norm.split('/').filter(Boolean).pop()
		if (lastSeg) {
			try {
				const re = new RegExp(`/(?:${lastSeg})(?:/|$)`) // граничим сегмент
				if (re.test(location.pathname)) return true
			} catch {}
		}
		return false
	}

	const isActiveExact = (base?: string) => {
		if (!base) return false
		const norm = normalizePath(base)
		return (
			location.pathname === norm ||
			Boolean(matchPath({ path: norm, end: true }, location.pathname))
		)
	}

	// Правило подсветки:
	// - для корневых разделов вида "/feed" (1 сегмент) — только exact
	// - для секционных пунктов вида "/feed/groups" (2+ сегмента) — deep (включая вложенные, напр. "/feed/groups/9")
	// - исключения: некоторые односегментные пути (например, "/settings") тоже подсвечиваются глубоко
	const segmentCount = getSegmentCount(to)
	const normTo = normalizePath(to)
	const forceDeepForOneSeg = DEEP_FOR_ONE_SEGMENT.has(normTo)
	let isLeafActive =
		segmentCount >= 2 || forceDeepForOneSeg
			? isActiveDeep(to)
			: isActiveExact(to)
	const isGroupActive = Array.isArray(subMenuItems)
		? subMenuItems.some(child => isActiveDeep(child.key))
		: false
	// ВАЖНО: если открыт дочерний путь (например /feed/groups/9), родительский пункт "feed" не должен подсвечиваться
	const isActive = isLeafActive
	// Определяем контекст настроек
	const isSettingsContext =
		location.pathname.startsWith('/settings') ||
		location.pathname.startsWith('/feed/plan/edit')

	// Определяем пункт "Настройки"
	const isSettingsItem = to === '/settings'

	// В контексте настроек ВСЕ пункты кроме Settings не должны считаться активными
	if (isSettingsContext && !isSettingsItem) {
		isLeafActive = false
	}

	// Теперь вычисляем основной active
	let isMainRowActive = isLeafActive

	// В контексте настроек активен только Settings
	if (isSettingsContext) {
		isMainRowActive = isSettingsItem
	}

	const navigateToPath = useCallback(
		(path?: string) => {
			const target = path ?? to
			if (target && target.startsWith('/')) navigate(target)
		},
		[navigate, to],
	)

	const handleHoveredOnMenuItem = useCallback((isHovered: boolean) => {
		setHoveredOnMainItems(isHovered)
	}, [])

	const handleHoveredOnSecondaryItem = useCallback((index, isHovered) => {
		setHoveredOnSecondaryItems(prev => ({
			...prev,
			[index]: isHovered,
		}))
	}, [])

	const handleClickOnMenuItem = useCallback(() => {
		navigateToPath()
	}, [navigateToPath])
	return (
		<Stack direction='column'>
			<Stack
				direction='row'
				gap='8px'
				alignItems='center'
				onClick={handleClickOnMenuItem}
				onMouseLeave={() => handleHoveredOnMenuItem(false)}
				onMouseEnter={() => handleHoveredOnMenuItem(true)}
				sx={{
					padding: subMenuItems ? '8px 12px 4px' : '8px 12px',
					width: '100%',
					cursor: 'pointer',
					borderRadius: '12px',
					backgroundColor:
						!subMenuItems && isMainRowActive
							? COLORS.mainSecondaryOpacity
							: 'transparent',
				}}
			>
				{cloneElement(icon, {
					color:
						isMainRowActive || hoveredOnMainItems ? COLORS.main : COLORS.icon,
				})}
				{typeof textMenu === 'string' ? (
					<TypographyPrimary16B
						sx={{
							color:
								isMainRowActive || hoveredOnMainItems
									? COLORS.main
									: COLORS.black,
						}}
					>
						{textMenu}
					</TypographyPrimary16B>
				) : (
					textMenu
				)}
			</Stack>
			{subMenuItems && (
				<Box padding={'0px 12px 0px 30px'}>
					<Stack direction='column'>
						{subMenuItems?.map((children, index) => {
							// Отключаем подсветку дочерних пунктов в контексте настроек
							const isSettingsContext =
								location.pathname.startsWith('/settings') ||
								location.pathname.startsWith('/feed/plan/edit')

							const isCurrentPath = isSettingsContext
								? false
								: isActiveDeep(children.key)
							return (
								<Box
									key={children.value}
									onMouseLeave={() =>
										handleHoveredOnSecondaryItem(index, false)
									}
									onMouseEnter={() => handleHoveredOnSecondaryItem(index, true)}
									onClick={() => navigateToPath(children.key)}
									sx={{
										borderRadius: '12px',
										padding: '4px 8px',
										backgroundColor: isCurrentPath
											? COLORS.mainSecondaryOpacity
											: 'transparent',
									}}
								>
									<TypographyPrimary500
										sx={{
											cursor: 'pointer',
											color:
												isCurrentPath || hoveredOnSecondaryItems[index]
													? COLORS.main
													: COLORS.black,
										}}
									>
										{children.value}
									</TypographyPrimary500>
								</Box>
							)
						})}
					</Stack>
				</Box>
			)}
		</Stack>
	)
}
